/**
 * Theme-specific image sets for the 3 visual themes: mountain, weave, river.
 * Images are served from public/images/ and referenced by URL path.
 */

export interface ThemeImageSet {
  /** Primary event logo displayed in header / hero */
  logo: string;
  /** Secondary logo variant */
  logoAlt: string;
  /** Hero banner scene illustrations (large) */
  heroScenes: string[];
  /** Scene illustrations for section backgrounds / cards */
  scenes: string[];
  /** Small character icons for competitions / activities */
  characters: string[];
  /** Cultural item icons for decoration */
  icons: string[];
  /** Line art icons for section dividers / accents */
  lineart: string[];
  /** Athlete character illustrations */
  athletes: string[];
  /** Real event photos */
  photos: string[];
}

const mountain: ThemeImageSet = {
  logo: '/images/logos/logo-village-circle.png',
  logoAlt: '/images/logos/logo-rainbow-river.png',
  heroScenes: [
    '/images/scenes/scene-archery-group.png',
    '/images/scenes/scene-mountain-archery.png',
    '/images/scenes/scene-forest-archery.png',
  ],
  scenes: [
    '/images/scenes/scene-archery-village.png',
    '/images/scenes/scene-village-life.png',
    '/images/scenes/scene-archer-aim.png',
    '/images/scenes/scene-river-village.png',
  ],
  characters: [
    '/images/characters/char-archer.png',
    '/images/characters/char-hunter.png',
    '/images/characters/char-tug-of-war.png',
    '/images/characters/char-runner.png',
  ],
  icons: [
    '/images/icons/icon-bow-arrow.png',
    '/images/icons/icon-deer.png',
    '/images/icons/icon-mountain-herb.png',
    '/images/icons/icon-gourd.png',
    '/images/icons/icon-spear.png',
    '/images/icons/icon-forest-mushroom.png',
  ],
  lineart: [
    '/images/lineart/lineart-runner-brown-01.png',
    '/images/lineart/lineart-runner-brown-02.png',
    '/images/lineart/lineart-runner-brown-03.png',
    '/images/lineart/lineart-runner-brown-04.png',
    '/images/lineart/lineart-tattoo-portrait-02.png',
  ],
  athletes: [
    '/images/athletes/athlete-runner.png',
    '/images/athletes/athlete-archer.png',
    '/images/athletes/athlete-climber.png',
    '/images/athletes/athlete-torch.png',
  ],
  photos: [
    '/images/photos/photo-archery-hunting.png',
    '/images/photos/photo-event-ceremony.png',
    '/images/photos/photo-event-opening.png',
  ],
};

const weave: ThemeImageSet = {
  logo: '/images/logos/logo-mountain-badge.png',
  logoAlt: '/images/logos/logo-sacred-flame.png',
  heroScenes: [
    '/images/scenes/scene-traditional-archery.png',
    '/images/scenes/scene-archery-practice.png',
    '/images/scenes/scene-carrying-baskets.png',
  ],
  scenes: [
    '/images/scenes/scene-tug-of-war.png',
    '/images/scenes/scene-archery-group.png',
    '/images/scenes/scene-archer-aim.png',
    '/images/scenes/scene-mountain-archery.png',
  ],
  characters: [
    '/images/characters/char-weaver.png',
    '/images/characters/char-dancer.png',
    '/images/characters/char-archer.png',
    '/images/characters/char-tug-of-war.png',
  ],
  icons: [
    '/images/icons/icon-weaving-loom.png',
    '/images/icons/icon-textile.png',
    '/images/icons/icon-basket.png',
    '/images/icons/icon-ornament.png',
    '/images/icons/icon-headdress.png',
    '/images/icons/icon-necklace.png',
  ],
  lineart: [
    '/images/lineart/lineart-runner-red-01.png',
    '/images/lineart/lineart-archer-red-01.png',
    '/images/lineart/lineart-runner-red-02.png',
    '/images/lineart/lineart-runner-red-03.png',
    '/images/lineart/lineart-tattoo-loom.png',
  ],
  athletes: [
    '/images/athletes/athlete-wrestler.png',
    '/images/athletes/athlete-thrower.png',
    '/images/athletes/athlete-spear.png',
    '/images/athletes/athlete-jumper.png',
  ],
  photos: [
    '/images/photos/photo-traditional-sport.png',
    '/images/photos/photo-cultural-event.png',
    '/images/photos/photo-event-banner.png',
  ],
};

const river: ThemeImageSet = {
  logo: '/images/logos/logo-spear-river.png',
  logoAlt: '/images/logos/logo-river-shield.png',
  heroScenes: [
    '/images/scenes/scene-river-village.png',
    '/images/scenes/scene-village-life.png',
    '/images/scenes/scene-archery-village.png',
  ],
  scenes: [
    '/images/scenes/scene-forest-archery.png',
    '/images/scenes/scene-carrying-baskets.png',
    '/images/scenes/scene-traditional-archery.png',
    '/images/scenes/scene-tug-of-war.png',
  ],
  characters: [
    '/images/characters/char-runner.png',
    '/images/characters/char-archer.png',
    '/images/characters/char-dancer.png',
    '/images/characters/char-hunter.png',
  ],
  icons: [
    '/images/icons/icon-fish.png',
    '/images/icons/icon-river-fish.png',
    '/images/icons/icon-bamboo-shoot.png',
    '/images/icons/icon-pottery.png',
    '/images/icons/icon-instrument.png',
    '/images/icons/icon-tool.png',
  ],
  lineart: [
    '/images/lineart/lineart-face-01.png',
    '/images/lineart/lineart-face-02.png',
    '/images/lineart/lineart-face-03.png',
    '/images/lineart/lineart-profile-01.png',
    '/images/lineart/lineart-profile-02.png',
  ],
  athletes: [
    '/images/athletes/athlete-paddler.png',
    '/images/athletes/athlete-runner.png',
    '/images/athletes/athlete-archer.png',
    '/images/athletes/athlete-torch.png',
  ],
  photos: [
    '/images/photos/photo-event-logo.png',
    '/images/photos/photo-archery-hunting.png',
    '/images/photos/photo-event-ceremony.png',
  ],
};

const themeImageSets: Record<string, ThemeImageSet> = { mountain, weave, river };

export function getThemeImages(theme: string): ThemeImageSet {
  return themeImageSets[theme] ?? mountain;
}

/** All unique images across all themes (for preloading / media gallery) */
export const allPhotos = [
  '/images/photos/photo-event-ceremony.png',
  '/images/photos/photo-event-opening.png',
  '/images/photos/photo-archery-hunting.png',
  '/images/photos/photo-traditional-sport.png',
  '/images/photos/photo-cultural-event.png',
  '/images/photos/photo-event-logo.png',
  '/images/photos/photo-event-banner.png',
];

export const allScenes = [
  '/images/scenes/scene-archery-group.png',
  '/images/scenes/scene-village-life.png',
  '/images/scenes/scene-archery-village.png',
  '/images/scenes/scene-archery-practice.png',
  '/images/scenes/scene-archer-aim.png',
  '/images/scenes/scene-mountain-archery.png',
  '/images/scenes/scene-forest-archery.png',
  '/images/scenes/scene-river-village.png',
  '/images/scenes/scene-carrying-baskets.png',
  '/images/scenes/scene-tug-of-war.png',
  '/images/scenes/scene-traditional-archery.png',
];
